// Shared logic for the Network screens — the list page, the edit form and the
// detail view all read from here so the colour maths, label maps and form
// shaping cannot drift between three copies.

import { buildSharingFormValues, buildSharingPayload } from "./sharing";

export const COLOR_PATTERN = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;
export const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const SLUG_PATTERN = /^[a-z0-9-]{2,30}$/;
export const ASSISTANT_NAME_MAX_LENGTH = 60;

export const LOGO_ACCEPT =
  "image/png,image/jpeg,image/jpg,image/webp,image/svg+xml";

export const STATUS_SELECT_OPTIONS = ["active", "suspended", "inactive"].map(
  (status) => ({
    label: status.charAt(0).toUpperCase() + status.slice(1),
    value: status,
  }),
);

export const SMS_PROVIDER_OPTIONS = [
  { label: "Intelli SMS", value: "intellisms" },
  { label: "Twilio", value: "twilio" },
];

export const COLOR_PRESETS = [
  "#000000",
  "#FFFFFF",
  "#1A1A2E",
  "#2C3E50",
  "#8B4513",
  "#C0392B",
  "#D4AF37",
  "#6B7280",
];

// ─── Colour ───────────────────────────────────────────────────────────────────

// A stored colour can be empty or malformed, and handing that straight to a
// style attribute renders nothing at all. Fall back so the swatch still reads.
export const safeColor = (value, fallback) =>
  COLOR_PATTERN.test(value || "") ? value : fallback;

// Perceived brightness, so text on a brand colour stays legible whether the
// network picked slate navy or a pale sand.
export const isDarkColor = (hex) => {
  const sanitized = String(hex || "").replace("#", "");

  if (sanitized.length !== 3 && sanitized.length !== 6) {
    return true;
  }

  const part = (index) =>
    sanitized.length === 3
      ? sanitized[index] + sanitized[index]
      : sanitized.slice(index * 2, index * 2 + 2);

  const red = parseInt(part(0), 16);
  const green = parseInt(part(1), 16);
  const blue = parseInt(part(2), 16);

  return (red * 299 + green * 587 + blue * 114) / 1000 < 128;
};

export const normalizeHexInput = (rawValue) => {
  const cleaned = String(rawValue || "")
    .replace(/[^0-9a-f]/gi, "")
    .slice(0, 6);
  return cleaned ? `#${cleaned.toUpperCase()}` : "";
};

// ─── Labels and states ────────────────────────────────────────────────────────

// Matches BRAND_ASSET_CATEGORIES in utils/brandAsset.js, in the same order.
export const BRAND_ASSET_LABELS = {
  logo: "Logo",
  letterhead: "Letterhead",
  signboard: "Signboard",
  brochure: "Brochure",
  mailcard: "Mailcard",
  signboardWriters: "Signboard writers",
  directionalSigns: "Directional signs",
  flagsBanners: "Flags & banners",
  businessCards: "Business cards",
  presentationFolders: "Presentation folders",
  largeFormatDecals: "Large format decals",
  portalBanners: "Portal banners",
};

export const BRAND_ASSET_CATEGORIES = Object.keys(BRAND_ASSET_LABELS);

export const BRAND_ASSET_CATEGORY_COUNT = BRAND_ASSET_CATEGORIES.length;

// Stored as uploaded, so the list is what a designer can actually open rather
// than what the image pipeline can compress.
export const BRAND_ASSET_ACCEPT =
  "image/png,image/jpeg,image/jpg,application/pdf,application/postscript,application/illustrator,.ai,.eps";

// Which variant a file is. Mirrors the tags the app offers per category.
export const BRAND_ASSET_TAGS = {
  logo: ["Primary", "Secondary"],
  letterhead: ["Standard"],
  signboard: ["Standard", "Premium", "Mini"],
  brochure: ["Listing Brochure", "Company Profile"],
  mailcard: ["Just Listed", "Just Sold"],
  signboardWriters: ["Primary", "Secondary"],
  directionalSigns: ["Arrow Sign", "Open Home", "Auction"],
  flagsBanners: ["Pull-up Banner", "Feather Flag", "Teardrop"],
  businessCards: ["Front", "Back", "Double-sided"],
  presentationFolders: ["Standard", "Premium"],
  largeFormatDecals: ["Window", "Vehicle", "Wall"],
  portalBanners: ["REA", "Domain", "Other"],
};

export const ASSET_STATUS = {
  ready: { label: "Ready", className: "bg-green-100 text-green-800" },
  inDesign: { label: "In design", className: "bg-blue-100 text-blue-800" },
  pending: { label: "Pending", className: "bg-yellow-100 text-yellow-800" },
};

// Order the design work moves through, not the object's key order.
export const BRAND_ASSET_STATUS_OPTIONS = ["pending", "inDesign", "ready"].map(
  (status) => ({ label: ASSET_STATUS[status].label, value: status }),
);

export const STATUS_PILL = {
  active: "bg-green-100 text-green-800",
  suspended: "bg-red-100 text-red-800",
  inactive: "bg-lightgray text-darkergray",
};

// How deep a setting may be set. A division may narrow its own entry, never
// widen it — see backend utils/controlledBy.js.
export const CONTROL_LEVELS = [
  { value: "network", label: "Network only" },
  { value: "division", label: "Division" },
  { value: "group", label: "Group" },
];

export const DEFAULT_CONTROL_LEVEL = "network";

// The settings that resolve down the tree. Order is the order they appear in
// the form, the view and the table; adding one here surfaces it in all three,
// alongside the matching path in Network.controlledBy.
export const GROUP_OVERRIDE_SETTINGS = [
  {
    key: "templates",
    name: "Marketing templates",
    description: "Social, post-list and prelist templates",
  },
  {
    key: "brandAssets",
    name: "Brand assets",
    description:
      "Signboards, brochures and mail cards — an override replaces the whole list",
  },
  {
    key: "toneOfVoice",
    name: "Tone of voice",
    description: "Letters, SMS, mailcards and proposals",
  },
  {
    key: "operationalEmails",
    name: "Operational emails",
    description: "Settlements, concierge, key events, accounts and admin",
  },
  {
    key: "compliance",
    name: "Compliance rules",
    description:
      "Rule set and restricted days — an override replaces the whole list",
  },
];

// controlledBy is stored as [{ path, level }]. The screens want it per setting,
// so flatten it — exact path match only, since the form never writes leaf paths.
export const getControlLevels = (network) => {
  const entries = Array.isArray(network?.controlledBy) ? network.controlledBy : [];
  return GROUP_OVERRIDE_SETTINGS.reduce((acc, setting) => {
    const match = entries.find((entry) => entry.path === setting.key);
    acc[setting.key] = match?.level || DEFAULT_CONTROL_LEVEL;
    return acc;
  }, {});
};

// The settings a group may set for itself — what the old allowGroupOverride
// list showed.
export const getAllowedOverrides = (network) => {
  const levels = getControlLevels(network);
  return GROUP_OVERRIDE_SETTINGS.filter(
    (setting) => levels[setting.key] === "group",
  );
};

// Sharing is one field at every level — see utils/sharing.js. Re-exported so
// the network screens have one import.
export {
  SHARED_RECORD_TYPES,
  SHARING_STATES,
  DEFAULT_SHARING_STATE,
  getSharedRecordTypes,
  getClosedRecordTypes,
} from "./sharing";

// ─── SMS ──────────────────────────────────────────────────────────────────────

// Networks created before `smsProvider` existed only carry one provider's
// credentials, so infer it from whichever set is actually stored.
export const resolveSmsProvider = (sms = {}) => {
  if (sms.smsProvider) return sms.smsProvider;
  if (sms.twilioAccountSid || sms.twilioAuthToken) return "twilio";
  return "intellisms";
};

// Only the selected provider's credentials go to the backend — sending the
// other provider's keys alongside them would store stale, unusable config.
const buildSmsPayload = (sms = {}) => {
  const smsProvider = resolveSmsProvider(sms);
  const shared = { smsProvider, alphaSenderId: sms.alphaSenderId || "" };

  if (smsProvider === "twilio") {
    return {
      ...shared,
      twilioAccountSid: sms.twilioAccountSid || "",
      twilioAuthToken: sms.twilioAuthToken || "",
    };
  }

  return {
    ...shared,
    username: sms.username || "",
    password: sms.password || "",
  };
};

// ─── Form shaping ─────────────────────────────────────────────────────────────

export const buildNetworkFormValues = (initialData = {}) => ({
  name: initialData.name || "",
  slug: initialData.slug || "",
  status: initialData.status || "active",
  // A date input holds just the day; the server keeps the end of it.
  trialEndsAt: initialData.trialEndsAt
    ? String(initialData.trialEndsAt).slice(0, 10)
    : "",
  aiAssistant: {
    name: initialData.aiAssistant?.name || "",
    picture: initialData.aiAssistant?.picture || "",
  },
  branding: {
    logos: {
      primary: initialData.branding?.logos?.primary || "",
      secondary: initialData.branding?.logos?.secondary || "",
    },
    colors: {
      primary: initialData.branding?.colors?.primary || "",
      secondary: initialData.branding?.colors?.secondary || "",
      accent: initialData.branding?.colors?.accent || "",
      header: initialData.branding?.colors?.header || "",
    },
    fonts: {
      heading: initialData.branding?.fonts?.heading || "",
      body: initialData.branding?.fonts?.body || "",
    },
    socialLinks: {
      facebook: initialData.branding?.socialLinks?.facebook || "",
      instagram: initialData.branding?.socialLinks?.instagram || "",
      linkedin: initialData.branding?.socialLinks?.linkedin || "",
      youtube: initialData.branding?.socialLinks?.youtube || "",
    },
    privacyPolicyUrl: initialData.branding?.privacyPolicyUrl || "",
  },
  operationalEmails: {
    settlements: initialData.operationalEmails?.settlements || "",
    concierge: initialData.operationalEmails?.concierge || "",
    keyEvents: initialData.operationalEmails?.keyEvents || "",
    accounts: initialData.operationalEmails?.accounts || "",
    admin: initialData.operationalEmails?.admin || "",
  },
  integrations: {
    sms: {
      smsProvider: resolveSmsProvider(initialData.integrations?.sms),
      username: initialData.integrations?.sms?.username || "",
      password: initialData.integrations?.sms?.password || "",
      twilioAccountSid: initialData.integrations?.sms?.twilioAccountSid || "",
      twilioAuthToken: initialData.integrations?.sms?.twilioAuthToken || "",
      alphaSenderId: initialData.integrations?.sms?.alphaSenderId || "",
    },
  },
  // Held as a map in the form, converted back to [{ path, level }] on submit.
  controlledBy: getControlLevels(initialData),
  sharing: buildSharingFormValues(initialData.sharing),
});

// The submitted payload. Logo URLs and brand assets are resolved by the caller
// because the uploads are async; everything else is shaped here.
//
// brandAssets is the COMPLETE list every time — the backend replaces the whole
// array, so a partial one drops the categories it left out.
export const buildNetworkPayload = (
  data,
  { primaryLogo, secondaryLogo, brandAssets },
) => ({
  ...data,
  branding: {
    ...data.branding,
    logos: { primary: primaryLogo, secondary: secondaryLogo },
  },
  integrations: {
    ...data.integrations,
    sms: buildSmsPayload(data.integrations?.sms),
  },
  controlledBy: buildControlledByPayload(data.controlledBy),
  sharing: buildSharingPayload(data.sharing),
  // Blank is sent as blank: the backend stores null, which means inherit. The
  // address is never sent, so one set directly in the database survives a save.
  aiAssistant: {
    name: data.aiAssistant?.name?.trim() || "",
    picture: data.aiAssistant?.picture?.trim() || "",
  },
  ...(brandAssets && { brandAssets }),
});

// Map back to [{ path, level }]. Network-only entries are omitted — an unlisted
// path already resolves to network, so writing them would be noise.
const buildControlledByPayload = (levels = {}) =>
  GROUP_OVERRIDE_SETTINGS.filter(
    (setting) => levels[setting.key] && levels[setting.key] !== DEFAULT_CONTROL_LEVEL,
  ).map((setting) => ({ path: setting.key, level: levels[setting.key] }));

// ─── Logo upload state ────────────────────────────────────────────────────────

export const buildLogoState = (url = "") => ({
  file: null,
  previewUrl: "",
  uploadedUrl: url || "",
  removed: false,
});

export const getVisibleLogoUrl = (logo) =>
  logo.previewUrl || (logo.removed ? "" : logo.uploadedUrl);

export const isImageFile = (file) =>
  Boolean(file) &&
  (file.type.startsWith("image/") || /\.svg$/i.test(file.name));

// The upload endpoint has returned a bare string, an object and an array of
// objects at different times, so accept all three shapes.
export const getUploadedUrl = (result) => {
  const item = Array.isArray(result) ? result[0] : result;

  if (!item) return null;
  if (typeof item === "string") return item;
  if (typeof item === "object") {
    return item.url || item.key || item.fileUrl || null;
  }

  return null;
};

export const logoUploadFolder = (network, slug) =>
  `networks/${network?._id || slug || "draft"}/logos`;

// ─── Brand asset upload state ─────────────────────────────────────────────────

export const brandAssetUploadFolder = (network, slug, category) =>
  `networks/${network?._id || slug || "draft"}/brandAssets/${category}`;

// The app writes group assets here, so admin uploads join the same folder.
export const groupBrandAssetUploadFolder = (organizationId, category) =>
  `brandAssets/${organizationId}/${category}`;

// Back to the stored shape. Categories with no files are dropped.
export const toBrandAssetArray = (state = {}) =>
  BRAND_ASSET_CATEGORIES.reduce((entries, category) => {
    const entry = state[category];
    if (!entry?.files?.length) return entries;

    entries.push({
      category,
      files: entry.files,
      status: entry.status || "pending",
      submittedAt: entry.submittedAt || new Date().toISOString(),
    });
    return entries;
  }, []);

// The stored array, keyed by category so a tab can read one without scanning.
export const buildBrandAssetState = (brandAssets = []) =>
  (brandAssets || []).reduce((accumulator, asset) => {
    accumulator[asset.category] = {
      status: asset.status || "pending",
      submittedAt: asset.submittedAt || "",
      files: (asset.files || []).map((file) => ({
        fileUrl: file.fileUrl,
        originalName: file.originalName || "",
        tag: file.tag || "",
        // Carried back on save: the whole array is replaced, so a file sent
        // without its timestamp would be re-dated to the save. Omitted rather
        // than defaulted — an empty string stores null, not the default.
        ...(file.uploadedAt && { uploadedAt: file.uploadedAt }),
      })),
    };
    return accumulator;
  }, {});

// ─── Form field plumbing ──────────────────────────────────────────────────────

export const inputClassName = (error) =>
  `form-input border ${error ? "border-red-500" : "border-mediumgray"}`;

export const getFieldError = (errors, path) =>
  path.split(".").reduce((accumulator, key) => accumulator?.[key], errors)
    ?.message;

export const optionalEmail = (label) => (value) =>
  !value || EMAIL_PATTERN.test(value) || `${label} must be a valid email`;

export const optionalColor = (label) => (value) =>
  !value || COLOR_PATTERN.test(value) || `${label} must be a valid hex color`;

// ─── Dates ────────────────────────────────────────────────────────────────────

export const formatNetworkDate = (value) => {
  if (!value) return "-";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";

  return date.toLocaleDateString("en-AU", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

export const formatNetworkDateTime = (value) => {
  if (!value) return "-";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);

  return date.toLocaleString("en-AU", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};
